import { debugLog } from './matchmakingConfig.js';

/**
 * Unit types for matchmaking
 * - 'single': A single player
 * - 'partnership': Two players in a partnership (must always play together)
 * - 'scheduled': Four players in a scheduled game (pre-formed teams)
 */
const UNIT_TYPES = {
  SINGLE: 'single',
  PARTNERSHIP: 'partnership',
  SCHEDULED: 'scheduled',
};

/**
 * Create a single player unit
 * @param {string} playerId - Player ID
 * @returns {Object} Unit object
 */
function createSingleUnit(playerId) {
  return {
    type: UNIT_TYPES.SINGLE,
    players: [playerId],
    id: playerId,
  };
}

/**
 * Create a partnership unit
 * @param {string} player1Id - First partner ID
 * @param {string} player2Id - Second partner ID
 * @returns {Object} Unit object
 */
function createPartnershipUnit(player1Id, player2Id) {
  // Sort IDs to ensure consistent unit ID
  const sortedIds = [player1Id, player2Id].sort();
  return {
    type: UNIT_TYPES.PARTNERSHIP,
    players: sortedIds,
    id: `partnership-${sortedIds[0]}-${sortedIds[1]}`,
  };
}

/**
 * Create a scheduled game unit (4 players)
 * @param {string[]} playerIds - Array of 4 player IDs
 * @returns {Object} Unit object
 */
function createScheduledUnit(playerIds) {
  if (playerIds.length !== 4) {
    throw new Error('Scheduled unit must have exactly 4 players');
  }
  // Sort IDs to ensure consistent unit ID
  const sortedIds = [...playerIds].sort();
  return {
    type: UNIT_TYPES.SCHEDULED,
    players: sortedIds,
    id: `scheduled-${sortedIds.join('-')}`,
  };
}

/**
 * Get the size (number of players) in a unit
 * @param {Object} unit - Unit object
 * @returns {number} Number of players (1, 2, or 4)
 */
function getUnitSize(unit) {
  if (!unit || !unit.players) return 0;
  return unit.players.length;
}

/**
 * Get player IDs from a unit
 * @param {Object} unit - Unit object
 * @returns {string[]} Array of player IDs
 */
function getUnitPlayers(unit) {
  if (!unit || !unit.players) return [];
  return [...unit.players];
}

/**
 * Build units from player IDs and partnerships
 * Converts individual players to units, grouping partners together
 * @param {string[]} playerIds - Array of player IDs
 * @param {Array} partnerships - Array of partnership objects
 * @returns {Object[]} Array of units
 */
function buildUnitsFromPlayers(playerIds, partnerships = []) {
  if (!playerIds || playerIds.length === 0) return [];

  const units = [];
  const processedPlayers = new Set();
  const partnershipMap = new Map();

  // Build partnership map for quick lookup
  if (partnerships && partnerships.length > 0) {
    partnerships.forEach(p => {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      if (p1 && p2) {
        partnershipMap.set(p1, p2);
        partnershipMap.set(p2, p1);
      }
    });
    debugLog('🤝 [UNIT BUILDER] Partnership map created with', partnershipMap.size, 'entries');
    debugLog('🤝 [UNIT BUILDER] Partnership pairs:', Array.from(partnershipMap.entries()).map(([id, partnerId]) => `${id.substring(0,8)} ↔ ${partnerId.substring(0,8)}`));
  }

  // Process each player
  playerIds.forEach(playerId => {
    if (processedPlayers.has(playerId)) return;

    const partnerId = partnershipMap.get(playerId);

    if (partnerId && playerIds.includes(partnerId)) {
      // Both partners are in the player list - create partnership unit
      debugLog(`🤝 [UNIT BUILDER] Creating partnership unit: ${playerId.substring(0,8)} + ${partnerId.substring(0,8)}`);
      const unit = createPartnershipUnit(playerId, partnerId);
      units.push(unit);
      processedPlayers.add(playerId);
      processedPlayers.add(partnerId);
    } else {
      // No partner or partner not in list - create single unit
      if (partnerId) {
        debugLog(`⚠️ [UNIT BUILDER] Player ${playerId.substring(0,8)} has partner ${partnerId.substring(0,8)}, but partner NOT in player list. Creating single unit.`);
      }
      const unit = createSingleUnit(playerId);
      units.push(unit);
      processedPlayers.add(playerId);
    }
  });

  debugLog('🔧 buildUnitsFromPlayers:', {
    inputPlayers: playerIds.length,
    outputUnits: units.length,
    partnerships: units.filter(u => u.type === UNIT_TYPES.PARTNERSHIP).length,
    singles: units.filter(u => u.type === UNIT_TYPES.SINGLE).length,
  });

  return units;
}

/**
 * Expand units back to player IDs
 * @param {Object[]} units - Array of units
 * @returns {string[]} Array of player IDs
 */
function expandUnitsToPlayers(units) {
  if (!units || units.length === 0) return [];

  const playerIds = [];
  units.forEach(unit => {
    const unitPlayers = getUnitPlayers(unit);
    playerIds.push(...unitPlayers);
  });

  return playerIds;
}

/**
 * Validate that all partnership units are complete
 * Ensures that if a partnership unit exists, both players are present
 * @param {Object[]} units - Array of units
 * @param {Array} partnerships - Array of partnership objects (for validation)
 * @returns {boolean} True if all units are valid
 */
function validateUnitIntegrity(units, partnerships = []) {
  if (!units || units.length === 0) return true;

  const partnershipMap = new Map();
  if (partnerships && partnerships.length > 0) {
    partnerships.forEach(p => {
      const p1 = p.player1Id || p.player1_id;
      const p2 = p.player2Id || p.player2_id;
      if (p1 && p2) {
        partnershipMap.set(p1, p2);
        partnershipMap.set(p2, p1);
      }
    });
  }

  // Check each unit
  for (const unit of units) {
    if (unit.type === UNIT_TYPES.PARTNERSHIP) {
      // Partnership unit must have exactly 2 players
      if (unit.players.length !== 2) {
        console.error('❌ Invalid partnership unit:', unit);
        return false;
      }

      // Verify these players are actually partners
      const [p1, p2] = unit.players;
      const expectedPartner1 = partnershipMap.get(p1);
      const expectedPartner2 = partnershipMap.get(p2);
      if (expectedPartner1 !== p2 || expectedPartner2 !== p1) {
        console.error('❌ Partnership unit contains non-partners:', unit);
        return false;
      }
    } else if (unit.type === UNIT_TYPES.SINGLE) {
      // Single unit must have exactly 1 player
      if (unit.players.length !== 1) {
        console.error('❌ Invalid single unit:', unit);
        return false;
      }
    } else if (unit.type === UNIT_TYPES.SCHEDULED) {
      // Scheduled unit must have exactly 4 players
      if (unit.players.length !== 4) {
        console.error('❌ Invalid scheduled unit:', unit);
        return false;
      }
    }
  }

  return true;
}

/**
 * Filter units based on a predicate function
 * The predicate receives the unit and should return true to keep it
 * @param {Object[]} units - Array of units
 * @param {Function} predicate - Function(unit) => boolean
 * @returns {Object[]} Filtered units
 */
function filterUnits(units, predicate) {
  if (!units || units.length === 0) return [];
  return units.filter(predicate);
}

/**
 * Check if a unit contains a specific player
 * @param {Object} unit - Unit object
 * @param {string} playerId - Player ID to check
 * @returns {boolean} True if unit contains player
 */
function unitContainsPlayer(unit, playerId) {
  if (!unit || !unit.players) return false;
  return unit.players.includes(playerId);
}

/**
 * Get total player count from an array of units
 * @param {Object[]} units - Array of units
 * @returns {number} Total number of players
 */
function getTotalPlayerCount(units) {
  if (!units || units.length === 0) return 0;
  return units.reduce((total, unit) => total + getUnitSize(unit), 0);
}

export {
  UNIT_TYPES,
  createSingleUnit,
  createPartnershipUnit,
  createScheduledUnit,
  getUnitSize,
  getUnitPlayers,
  buildUnitsFromPlayers,
  expandUnitsToPlayers,
  validateUnitIntegrity,
  filterUnits,
  unitContainsPlayer,
  getTotalPlayerCount,
};

